// src/pages/branch/BranchOrderDetailsPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import {
  Package,
  Truck,
  CheckCircle2,
  AlertCircle,
  Clock,
  History,
  FileText,
} from 'lucide-react';
import { ordersAPI } from '../../services/api';
import { Button } from '../../components/UI/Button';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// === دوال مساعدة ===

const translateUnit = (unit: string | undefined, isRtl: boolean): string => {
  if (!unit) return isRtl ? 'وحدة' : 'unit';
  const map: Record<string, string> = {
    'كيلو': 'كيلو', kg: 'كجم',
    'قطعة': 'قطعة', piece: 'قطعة',
    'علبة': 'علبة', pack: 'علبة',
    'صينية': 'صينية', tray: 'صينية',
  };
  return map[unit] || unit;
};

const toArabicNumerals = (str: string): string => {
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return str.replace(/\d/g, (d) => arabicNumerals[Number(d)]);
};

const formatDateTime = (dateString: string, isRtl: boolean): string => {
  return new Date(dateString).toLocaleString(isRtl ? 'ar-EG' : 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).replace(' AM', ' ص').replace(' PM', ' م');
};

const getTranslation = (key: string, isRtl: boolean): string => {
  const translations: Record<string, { ar: string; en: string }> = {
    order: { ar: 'طلب', en: 'Order' },
    items: { ar: 'المنتجات', en: 'Products' },
    requested: { ar: 'مطلوب', en: 'Requested' },
    received: { ar: 'مستلم', en: 'Received' },
    rejected: { ar: 'مرفوض', en: 'Rejected' },
    notes: { ar: 'ملاحظات الطلب', en: 'Order Notes' },
    status_history: { ar: 'سجل الحالة', en: 'Status History' },
    status_pending: { ar: 'قيد الانتظار', en: 'Pending' },
    status_approved: { ar: 'تمت الموافقة', en: 'Approved' },
    status_in_production: { ar: 'في الإنتاج', en: 'In Production' },
    status_completed: { ar: 'مكتمل', en: 'Completed' },
    status_in_transit: { ar: 'في النقل', en: 'In Transit' },
    status_delivered: { ar: 'تم التسليم', en: 'Delivered' },
    status_cancelled: { ar: 'ملغى', en: 'Cancelled' },
    product: { ar: 'المنتج', en: 'Product' },
    total_requested_qty: { ar: 'إجمالي الكمية المطلوبة', en: 'Total Requested Quantity' },
    total_received_qty: { ar: 'إجمالي الكمية المستلمة', en: 'Total Received Quantity' },
  };
  return translations[key]?.[isRtl ? 'ar' : 'en'] || key;
};

const loadAmiriFont = async (doc: jsPDF): Promise<boolean> => {
  const fontName = 'Amiri';
  const urls = {
    regular: 'https://raw.githubusercontent.com/aliftype/amiri/master/fonts/Amiri-Regular.ttf',
    bold: 'https://raw.githubusercontent.com/aliftype/amiri/master/fonts/Amiri-Bold.ttf',
  };
  const toBase64 = (buffer: ArrayBuffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return window.btoa(binary);
  };
  try {
    const [regRes, boldRes] = await Promise.all([fetch(urls.regular), fetch(urls.bold)]);
    if (!regRes.ok || !boldRes.ok) throw new Error('فشل تحميل خط Amiri');
    const [regBuffer, boldBuffer] = await Promise.all([regRes.arrayBuffer(), boldRes.arrayBuffer()]);
    doc.addFileToVFS('Amiri-Regular.ttf', toBase64(regBuffer));
    doc.addFileToVFS('Amiri-Bold.ttf', toBase64(boldBuffer));
    doc.addFont('Amiri-Regular.ttf', fontName, 'normal');
    doc.addFont('Amiri-Bold.ttf', fontName, 'bold');
    return true;
  } catch (err) {
    console.warn('فشل تحميل خط Amiri. استخدام الخط الافتراضي.', err);
    return false;
  }
};

const formatQuantityWithUnit = (qty: number, unit: string, isRtl: boolean): string => {
  return isRtl
    ? `${toArabicNumerals(qty.toString())} ${unit}`
    : `${qty} ${unit}`;
};

const downloadOrderDetailPDF = async (order: any, isRtl: boolean) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const fontLoaded = await loadAmiriFont(doc);
  const fontFamily = fontLoaded ? 'Amiri' : 'helvetica';
  doc.setFont(fontFamily);

  // العنوان
  doc.setFontSize(22);
  doc.setFont(fontFamily, 'bold');
  const title = isRtl ? `طلب رقم #${order.orderNumber}` : `Order #${order.orderNumber}`;
  doc.text(title, pageWidth / 2, 25, { align: 'center' });

  // معلومات الطلب
  doc.setFontSize(12);
  doc.setFont(fontFamily, 'normal');
  doc.text(isRtl ? `الفرع: ${order.branch.displayName}` : `Branch: ${order.branch.displayName}`, isRtl ? pageWidth - 20 : 20, 40, { align: isRtl ? 'right' : 'left' });
  doc.text(isRtl ? `التاريخ: ${order.date}` : `Date: ${order.date}`, isRtl ? pageWidth - 20 : 20, 48, { align: isRtl ? 'right' : 'left' });

  const statusText = getTranslation(`status_${order.status}`, isRtl);
  doc.setTextColor(180, 90, 0);
  doc.setFont(fontFamily, 'bold');
  doc.text(isRtl ? `الحالة: ${statusText}` : `Status: ${statusText}`, isRtl ? pageWidth - 20 : 20, 56, { align: isRtl ? 'right' : 'left' });
  doc.setTextColor(0);

  // خط فاصل
  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.5);
  doc.line(20, 68, pageWidth - 20, 68);

  // === جدول المنتجات (متوسط بالكامل) ===
  const tableHead = isRtl
    ? [getTranslation('received', isRtl), getTranslation('requested', isRtl), getTranslation('product', isRtl)]
    : [getTranslation('product', isRtl), getTranslation('requested', isRtl), getTranslation('received', isRtl)];

  const tableBody = order.items.map((item: any) => {
    const unit = item.displayUnit || (isRtl ? 'وحدة' : 'unit');
    const requestedDisplay = formatQuantityWithUnit(item.quantity, unit, isRtl);
    const receivedDisplay = item.rejected
      ? (isRtl ? 'مرفوض' : 'Rejected')
      : item.receivedQuantity > 0
        ? formatQuantityWithUnit(item.receivedQuantity, unit, isRtl)
        : (isRtl ? '—' : '—');

    const row = [item.displayProductName, requestedDisplay, receivedDisplay];
    return isRtl ? row.reverse() : row;
  });

  // ✅ حساب عرض الجدول الكلي يدويًا لتوسيطه
  const colWidths = [80, 35, 35]; // product, requested, received
  const totalTableWidth = colWidths.reduce((sum, w) => sum + w, 0);
  const marginLeft = (pageWidth - totalTableWidth) / 2;

  autoTable(doc, {
    head: [tableHead],
    body: tableBody,
    startY: 75,
    theme: 'grid',
    headStyles: {
      fillColor: [250, 230, 210],
      textColor: [30, 30, 30],
      fontStyle: 'bold',
      fontSize: 11,
      halign: 'center',
    },
    styles: {
      font: fontFamily,
      fontSize: 10,
      cellPadding: 6,
      halign: 'center', // ✅ توسيط جميع الخلايا (بما فيها "المنتج")
    },
    columnStyles: isRtl
      ? {
          0: { cellWidth: 35 },
          1: { cellWidth: 35 },
          2: { cellWidth: 80 },
        }
      : {
          0: { cellWidth: 80 },
          1: { cellWidth: 35 },
          2: { cellWidth: 35 },
        },
    margin: { left: marginLeft, right: marginLeft }, // ✅ توسيط الجدول في الصفحة
  });

  let y = (doc as any).lastAutoTable.finalY + 15;

  // === ملخص الكميات ===
  const fallbackUnit = order.items[0]?.displayUnit || (isRtl ? 'وحدة' : 'unit');
  doc.setFontSize(13);
  doc.setFont(fontFamily, 'bold');
  const quantitySummaryLabel = isRtl ? 'ملخص الكميات:' : 'Quantity Summary:';
  doc.text(quantitySummaryLabel, pageWidth / 2, y, { align: 'center' }); // ✅ توسيط العنوان
  y += 8;
  doc.setFont(fontFamily, 'normal');
  doc.setFontSize(12);

  const totalRequestedText = isRtl
    ? `إجمالي المطلوب: ${toArabicNumerals(order.totalRequestedQty.toString())} ${fallbackUnit}`
    : `Total Requested: ${order.totalRequestedQty} ${fallbackUnit}`;
  doc.text(totalRequestedText, pageWidth / 2, y, { align: 'center' }); // ✅ توسيط النص

  if (order.status === 'delivered') {
    y += 8;
    const totalReceivedText = isRtl
      ? `إجمالي المستلم: ${toArabicNumerals(order.totalReceivedQty.toString())} ${fallbackUnit}`
      : `Total Received: ${order.totalReceivedQty} ${fallbackUnit}`;
    doc.text(totalReceivedText, pageWidth / 2, y, { align: 'center' });
  }

  // === الملاحظات ===
  if (order.notes) {
    y += 15;
    if (y > pageHeight - 40) {
      doc.addPage();
      y = 20;
    }
    doc.setFontSize(12);
    doc.setFont(fontFamily, 'bold');
    doc.text(getTranslation('notes', isRtl) + ':', pageWidth / 2, y, { align: 'center' });
    doc.setFont(fontFamily, 'normal');
    doc.setFontSize(11);
    const splitNotes = doc.splitTextToSize(order.notes, pageWidth - 60);
    doc.text(splitNotes, pageWidth / 2, y + 8, { align: 'center' });
  }

  // === التذييل ===
  doc.setFontSize(9);
  doc.setTextColor(150);
  const footerText = isRtl
    ? `نظام إدارة الطلبات – ${order.branch.displayName} – #${order.orderNumber}`
    : `Order Management System – ${order.branch.displayName} – #${order.orderNumber}`;
  doc.text(footerText, pageWidth / 2, pageHeight - 12, { align: 'center' });

  const fileName = isRtl
    ? `${order.branch.displayName.replace(/\s+/g, '_')}_طلب_${order.orderNumber}.pdf`
    : `${order.branch.displayName.replace(/\s+/g, '_')}_Order_${order.orderNumber}.pdf`;
  doc.save(fileName);
  toast.success(isRtl ? 'تم تصدير الطلب بنجاح' : 'Order exported successfully');
};

// === المكون الرئيسي ===
export const BranchOrderDetailsPage: React.FC = () => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await ordersAPI.getById(id);

      let totalRequestedQty = 0;
      let totalReceivedQty = 0;

      const mappedItems = (res.items || []).map((item: any) => {
        const requestedQty = item.quantity || 0;
        const receivedQty = item.receivedQuantity || 0;

        totalRequestedQty += requestedQty;
        if (!item.rejected && !item.rejectedByBranch) {
          totalReceivedQty += receivedQty;
        }

        return {
          _id: item._id,
          displayProductName: isRtl ? item.product?.name || 'منتج غير معروف' : item.product?.nameEn || item.product?.name || 'Unknown Product',
          quantity: requestedQty,
          receivedQuantity: receivedQty,
          displayUnit: translateUnit(item.product?.unit, isRtl),
          rejected: !!item.rejected || !!item.rejectedByBranch,
        };
      });

      setOrder({
        ...res,
        id: res._id,
        orderNumber: res.orderNumber || 'N/A',
        totalRequestedQty,
        totalReceivedQty,
        branch: {
          displayName: isRtl ? res.branch?.name || 'غير معروف' : res.branch?.nameEn || res.branch?.name || 'Unknown',
        },
        date: formatDateTime(res.createdAt || new Date(), isRtl),
        items: mappedItems,
        statusHistory: (res.statusHistory || []).map((h: any) => ({
          ...h,
          changedAt: formatDateTime(h.changedAt || h.createdAt || new Date(), isRtl),
        })),
      });
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'فشل جلب الطلب';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [id, isRtl]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handleDownloadPDF = () => {
    if (order) downloadOrderDetailPDF(order, isRtl);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-600 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-gray-800 mb-2">
          {isRtl ? 'الطلب غير موجود' : 'Order not found'}
        </h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <Button onClick={() => navigate(-1)} size="lg">
          {isRtl ? 'العودة' : 'Go Back'}
        </Button>
      </div>
    );
  }

  const isDelivered = order.status === 'delivered';

  return (
    <div className="w-full min-h-screen mx-auto">
      <div className="mx-auto px-4 py-3">
        <div className="flex flex-col items-center text-center sm:flex-row sm:items-center sm:justify-between sm:text-start gap-3">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-lg font-bold text-gray-900">
                {isRtl ? `طلب #${order.orderNumber}` : `Order #${order.orderNumber}`}
              </h1>
              <p className="text-xs text-gray-500">{order.branch.displayName} • {order.date}</p>
            </div>
          </div>
          <Button onClick={handleDownloadPDF} variant="primary" size="sm" className="w-full sm:w-auto text-xs px-5">
            <FileText className="w-3.5 h-3.5 me-1.5" />
            {isRtl ? 'تصدير PDF' : 'Export PDF'}
          </Button>
        </div>
      </div>

      <div className="mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4">
            {/* Quantity Summary */}
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-5 border border-indigo-200 text-center">
              <p className="text-indigo-700 font-medium text-sm">{getTranslation('total_requested_qty', isRtl)}</p>
              <p className="text-2xl font-bold text-indigo-900 mt-1">
                {isRtl ? toArabicNumerals(order.totalRequestedQty.toString()) : order.totalRequestedQty}
              </p>
              {isDelivered && (
                <div className="mt-4 pt-4 border-t border-indigo-200">
                  <p className="text-emerald-700 font-medium text-sm">{getTranslation('total_received_qty', isRtl)}</p>
                  <p
                    className={`text-2xl font-bold mt-1 ${
                      order.totalReceivedQty < order.totalRequestedQty ? 'text-amber-600' : 'text-emerald-600'
                    }`}
                  >
                    {isRtl ? toArabicNumerals(order.totalReceivedQty.toString()) : order.totalReceivedQty}
                  </p>
                </div>
              )}
            </div>

            {/* Items List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="font-bold text-base flex items-center gap-2">
                  <Package className="w-4 h-4 text-amber-600" />
                  {getTranslation('items', isRtl)} ({order.items.length})
                </h3>
              </div>
              <div className="divide-y divide-gray-100">
                {order.items.map((item: any) => {
                  const showReceived = item.rejected || item.receivedQuantity > 0;
                  return (
                    <div key={item._id} className="p-5 hover:bg-gray-50 transition-colors">
                      <h4 className="font-semibold text-sm text-gray-900 mb-3">{item.displayProductName}</h4>
                      <div className="text-xs space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">{getTranslation('requested', isRtl)}:</span>
                          <span className="font-medium">
                            {isRtl
                              ? `${toArabicNumerals(item.quantity.toString())} ${item.displayUnit}`
                              : `${item.quantity} ${item.displayUnit}`}
                          </span>
                        </div>
                        {showReceived && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">{getTranslation('received', isRtl)}:</span>
                            <span
                              className={`font-bold ${
                                item.rejected
                                  ? 'text-red-600'
                                  : item.receivedQuantity < item.quantity
                                  ? 'text-amber-600'
                                  : 'text-emerald-600'
                              }`}
                            >
                              {item.rejected
                                ? getTranslation('rejected', isRtl)
                                : isRtl
                                ? `${toArabicNumerals(item.receivedQuantity.toString())} ${item.displayUnit}`
                                : `${item.receivedQuantity} ${item.displayUnit}`}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Notes */}
            {order.notes && (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-5">
                <h4 className="font-bold text-amber-900 text-sm mb-1.5 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> {getTranslation('notes', isRtl)}
                </h4>
                <p className="text-amber-800 text-sm leading-relaxed">{order.notes}</p>
              </div>
            )}
          </div>

          {/* Status History */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sticky top-24">
              <h3 className="font-bold text-base flex items-center gap-2 mb-4">
                <History className="w-4 h-4 text-amber-600" /> {getTranslation('status_history', isRtl)}
              </h3>
              <div className="space-y-4 relative">
                <div className={`absolute ${isRtl ? 'right-4' : 'left-4'} top-0 bottom-0 w-0.5 bg-gray-200`}></div>
                {order.statusHistory?.map((h: any, i: number, arr: any[]) => {
                  const isLast = i === arr.length - 1;
                  return (
                    <div key={i} className="relative flex gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center z-10 shrink-0 ${
                          isLast ? 'bg-amber-600 text-white' : 'bg-gray-300 text-gray-600'
                        }`}
                      >
                        {h.status === 'delivered' ? (
                          <Truck className="w-4 h-4" />
                        ) : h.status === 'completed' ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : h.status === 'pending' ? (
                          <Clock className="w-4 h-4" />
                        ) : (
                          <Package className="w-4 h-4" />
                        )}
                      </div>
                      <div className="text-sm">
                        <p className="font-semibold text-gray-800">{getTranslation(`status_${h.status}`, isRtl)}</p>
                        <p className="text-gray-500 text-xs">{h.changedAt}</p>
                        {h.notes && <p className="text-gray-600 italic mt-0.5 text-xs">{h.notes}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BranchOrderDetailsPage;